import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  AlertTriangle, BedDouble, Building2, CheckCircle2, ClipboardList, CreditCard,
  DollarSign, HardHat, PiggyBank, Sparkles, User, Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartCard, DataGrid, KpiCard, MetricStrip } from "@/components/dashboard";
import { formatCurrency, formatChartCurrency, formatPercent } from "@/lib/format";

type PricingTier = { type: string; fee: number; pay: number };
type IncomeRow = {
  id: string; name: string; bedrooms: number;
  cleaningIncome: number; cleanerPay: number; partnerPay: number;
  maintenanceIncome: number; jobCount: number; supplyCost: number;
};
type WorkerRow = { userId: number; name: string; role: string; jobCount: number; totalEarned: number; avgPerJob: number };

export type ServicesModeViewProps = {
  // KPIs
  grossServiceIncome: number;
  totalCleanerPayroll: number;
  totalPartnerCommissions: number;
  netServiceProfit: number;
  cleaningJobsCount: number;
  partnerName: string;

  // Cleaning status
  completedCleanings: number;
  pendingCleanings: number;
  inProgressCleanings: number;
  assignedCleanings: number;
  unassignedCleanings: number;

  // Pricing + tables
  pricingTiers: PricingTier[];
  incomeByApartment: IncomeRow[];
  earningsByWorker: WorkerRow[];

  // P&L extras
  totalCleaningIncome: number;
  totalMaintenanceIncome: number;
  checkInIncome: number;
  totalEstSupplyCost: number;
  orgExpenses?: number;
  partnerCommissionPct: string;
  hasCatalogData: boolean;
  cleaningRecordsCount: number;
  maintenanceRecordsCount: number;
};

function bedroomLabel(bedrooms: number): string {
  if (bedrooms === 0) return "Studio";
  return `${bedrooms} BR`;
}

export function ServicesModeView(props: ServicesModeViewProps) {
  const {
    grossServiceIncome, totalCleanerPayroll, totalPartnerCommissions, netServiceProfit,
    cleaningJobsCount, partnerName,
    completedCleanings, pendingCleanings, inProgressCleanings, assignedCleanings, unassignedCleanings,
    pricingTiers, incomeByApartment, earningsByWorker,
    totalCleaningIncome, totalMaintenanceIncome, checkInIncome, totalEstSupplyCost,
    orgExpenses = 0,
    partnerCommissionPct, hasCatalogData, cleaningRecordsCount, maintenanceRecordsCount,
  } = props;

  const marginPct = grossServiceIncome > 0 ? (netServiceProfit / grossServiceIncome) * 100 : 0;

  const pricingColumns = useMemo<ColumnDef<PricingTier & { margin: number; marginPct: number }, unknown>[]>(() => [
    { accessorKey: "type", header: "Apt Type", cell: info => <span className="font-medium">{String(info.getValue())}</span> },
    { accessorKey: "fee", header: "Fee Charged", cell: info => <span className="text-green-600">{formatCurrency(Number(info.getValue()))}</span> },
    { accessorKey: "pay", header: "Cleaner Pay", cell: info => <span className="text-red-500">{formatCurrency(Number(info.getValue()))}</span> },
    {
      accessorKey: "margin",
      header: "Margin",
      cell: info => {
        const v = Number(info.getValue());
        return <span className={`font-semibold ${v >= 0 ? "" : "text-red-500"}`}>{formatCurrency(v)}</span>;
      },
    },
    {
      accessorKey: "marginPct",
      header: "Margin %",
      cell: info => <span className="text-muted-foreground">{formatPercent(Number(info.getValue()))}</span>,
    },
  ], []);

  const pricingRows = useMemo(
    () => pricingTiers.map(t => {
      const margin = t.fee - t.pay;
      const pct = t.fee > 0 ? (margin / t.fee) * 100 : 0;
      return { ...t, margin, marginPct: pct };
    }),
    [pricingTiers],
  );

  type IncomeWithNet = IncomeRow & { totalIncome: number; netApt: number };
  const incomeColumns = useMemo<ColumnDef<IncomeWithNet, unknown>[]>(() => [
    { accessorKey: "name", header: "Apartment", cell: info => <span className="font-medium truncate block max-w-[120px]">{String(info.getValue())}</span> },
    {
      accessorKey: "bedrooms",
      header: "Type",
      cell: info => <Badge variant="outline" className="text-xs">{bedroomLabel(Number(info.getValue()))}</Badge>,
    },
    { accessorKey: "jobCount", header: "Jobs" },
    { accessorKey: "totalIncome", header: "Income", cell: info => <span className="text-green-600">{formatCurrency(Number(info.getValue()))}</span> },
    { accessorKey: "cleanerPay", header: "Cleaner Pay", cell: info => <span className="text-red-500">{formatCurrency(Number(info.getValue()))}</span> },
    { accessorKey: "supplyCost", header: "Supplies", cell: info => <span className="text-orange-500">{formatCurrency(Number(info.getValue()))}</span> },
    {
      accessorKey: "netApt",
      header: "Net",
      cell: info => {
        const v = Number(info.getValue());
        return <span className={`font-semibold ${v >= 0 ? "" : "text-red-500"}`}>{formatCurrency(v)}</span>;
      },
    },
  ], []);

  const incomeRows = useMemo<IncomeWithNet[]>(
    () => incomeByApartment.map(a => {
      const totalIncome = a.cleaningIncome + a.maintenanceIncome;
      const netApt = totalIncome - a.cleanerPay - a.partnerPay - a.supplyCost;
      return { ...a, totalIncome, netApt };
    }),
    [incomeByApartment],
  );

  const earningsChartData = earningsByWorker.slice(0, 12).map(w => ({ name: w.name, earned: Number(w.totalEarned.toFixed(2)) }));

  return (
    <>
      <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground flex items-start gap-2" data-testid="info-services-mode">
        <HardHat className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Services business view. Cleaning, maintenance, and check-ins are{" "}
          <strong className="text-foreground">income</strong>. Cleaners are paid per job based on apartment size.
        </span>
      </div>

      <MetricStrip cols={4}>
        <KpiCard
          label="Gross Income"
          value={formatCurrency(grossServiceIncome)}
          icon={DollarSign}
          variant="success"
          testId="card-svc-gross-income"
        />
        <KpiCard
          label="Payroll"
          value={formatCurrency(totalCleanerPayroll)}
          helper={`${cleaningJobsCount} jobs`}
          icon={Users}
          variant="danger"
          testId="card-svc-cleaner-payroll"
        />
        <KpiCard
          label="Commissions"
          value={formatCurrency(totalPartnerCommissions)}
          helper={partnerName}
          icon={CreditCard}
          variant="warning"
          testId="card-svc-partner-payout"
        />
        <KpiCard
          label="Net Profit"
          value={formatCurrency(netServiceProfit)}
          helper={formatPercent(marginPct)}
          icon={PiggyBank}
          variant={netServiceProfit >= 0 ? "success" : "danger"}
          testId="card-svc-net-profit"
        />
      </MetricStrip>

      <Card data-testid="card-cleaning-operations-svc">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Job Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <MetricStrip cols={5}>
            <KpiCard label="Completed" value={completedCleanings} icon={CheckCircle2} variant="success" />
            <KpiCard label="Pending" value={pendingCleanings} icon={ClipboardList} variant="warning" />
            <KpiCard label="In Progress" value={inProgressCleanings} icon={Sparkles} variant="info" />
            <KpiCard label="Assigned" value={assignedCleanings} icon={User} variant="default" />
            <KpiCard label="Unassigned" value={unassignedCleanings} icon={AlertTriangle} variant="danger" />
          </MetricStrip>
        </CardContent>
      </Card>

      <Card data-testid="card-pricing-tiers">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <BedDouble className="w-5 h-5 text-primary" />
            Pricing by Apartment Type
          </CardTitle>
          <p className="text-xs text-muted-foreground">Per-job rates from commission settings. Cleaners paid per job.</p>
        </CardHeader>
        <CardContent className="px-0">
          <DataGrid
            data={pricingRows}
            columns={pricingColumns}
            searchable={false}
            showDensityToggle={false}
            showPagination={false}
            initialDensity="compact"
            getRowId={r => r.type}
            className="border-0 shadow-none"
            testId="table-pricing-tiers"
          />
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card data-testid="card-income-by-apartment">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-muted-foreground" />
              Income by Apartment
            </CardTitle>
            <p className="text-xs text-muted-foreground">Service income, cleaner pay, and net per apartment</p>
          </CardHeader>
          <CardContent className="px-0">
            <DataGrid
              data={incomeRows}
              columns={incomeColumns}
              searchPlaceholder="Search apartment…"
              initialDensity="compact"
              showDensityToggle={false}
              pageSize={20}
              exportFilename="income-by-apartment"
              getRowId={r => r.id}
              emptyText="No cleaning records yet."
              className="border-0 shadow-none"
              testId="table-income-by-apt"
            />
          </CardContent>
        </Card>

        <ChartCard
          title="Cleaner Earnings (Per Job)"
          description="Each cleaner is paid per job completed"
          isEmpty={earningsChartData.length === 0}
          emptyText="No cleaner payments yet."
          height={Math.max(220, earningsChartData.length * 28 + 20)}
          testId="card-cleaner-earnings"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={earningsChartData} layout="vertical" margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={formatChartCurrency} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
              <Tooltip
                formatter={(v: number) => formatCurrency(v)}
                cursor={{ fill: "transparent" }}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="earned" radius={[0, 4, 4, 0]} fill="#6366f1" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <Card data-testid="card-services-pnl">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <PiggyBank className="w-5 h-5 text-primary" />
            Services P&L Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Income</p>
            <PnlRow color="bg-blue-500" label={`Cleaning (${cleaningRecordsCount} jobs)`} value={`+${formatCurrency(totalCleaningIncome)}`} positive />
            <PnlRow color="bg-orange-500" label={`Maintenance (${maintenanceRecordsCount} jobs)`} value={`+${formatCurrency(totalMaintenanceIncome)}`} positive />
            <PnlRow color="bg-purple-500" label="Check-in Fees" value={`+${formatCurrency(checkInIncome)}`} positive />
            <div className="border-t flex items-center justify-between gap-2 py-2">
              <span className="text-xs sm:text-sm font-semibold">Gross Income</span>
              <span className="text-xs sm:text-sm font-bold text-green-600 shrink-0">{formatCurrency(grossServiceIncome)}</span>
            </div>

            <div className="border-t pt-2" />
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Costs</p>
            <PnlRow color="bg-red-500" label={`Payroll (${earningsByWorker.length} cleaners)`} value={`-${formatCurrency(totalCleanerPayroll)}`} />
            <PnlRow color="bg-red-400" label={`Partner (${partnerCommissionPct}%)`} value={`-${formatCurrency(totalPartnerCommissions)}`} />
            <PnlRow color="bg-orange-400" label={`Supplies${hasCatalogData ? "" : " (est.)"}`} value={`-${formatCurrency(totalEstSupplyCost)}`} />
            {orgExpenses > 0 && (
              <PnlRow color="bg-red-600" label="Org Expenses (utilities, salaries)" value={`-${formatCurrency(orgExpenses)}`} />
            )}

            <div className="border-t-2 border-primary/30" />
            <div className="flex items-center justify-between py-2">
              <span className="text-base font-bold flex items-center gap-2">
                <PiggyBank className="w-4 h-4 text-primary" />
                Net Profit
              </span>
              <span className={`text-lg font-bold ${netServiceProfit >= 0 ? "text-green-600" : "text-red-500"}`}>
                {formatCurrency(netServiceProfit)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function PnlRow({ color, label, value, positive }: { color: string; label: string; value: string; positive?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <span className="text-xs sm:text-sm flex items-center gap-2 min-w-0">
        <div className={`w-3 h-3 rounded-full shrink-0 ${color}`} />
        <span className="truncate">{label}</span>
      </span>
      <span className={`text-xs sm:text-sm font-bold shrink-0 ${positive ? "text-green-600" : "text-red-500"}`}>{value}</span>
    </div>
  );
}

export default ServicesModeView;
